import moment from 'moment';

import { displayDateFormatNoYear, pendingOrdersDatePlacedFormat } from '../../../constants/date-time.constants';
import lang from '../../../language';
import { MarketCountryCode } from '../../../models/fundamental/types';
import { SOROrderSide, SOROrderType, TenNSOROrderStatus } from '../../../models/gateway/types';
import { formatCurrency, formatNumber } from '../../../util/DataHelpers';
import { getNextWorkday, isDateMarketDataWorkingDay } from '../../../util/DateTimeHelpers';
import { INVALID_VALUE, NullableString } from '../../../util/types';
import { COLORS } from '../constants';
import {
  AccountTradeHistoryResponseData,
  TradeHistoryParsedOrders,
} from '../types';

// Local copy to avoid importing heavy TradingHelpers (which pulls in store and services)
export const mapOrderType = (
  side: SOROrderSide,
  type: SOROrderType,
  isInPendingOrderTable = false,
): string => {
  if (!type || !side) return INVALID_VALUE;

  let result: NullableString = SOROrderType[type];

  switch (side) {
    case SOROrderSide.Buy:
      result += ` ${lang.commonOrderSideButtonBuy()}`;
      break;
    case SOROrderSide.Sell:
      result += ` ${lang.commonOrderSideButtonSell()}`;
      break;
    case SOROrderSide.SellShort:
      result += isInPendingOrderTable
        ? ` ${lang.commonShortSell()}`
        : ` ${lang.commonOrderSideButtonSell()}`;
      break;
    default:
      break;
  }

  return result ?? INVALID_VALUE;
};

export const parsedAccountTradeHistoryOrdersOrders = (
  orderResponse: AccountTradeHistoryResponseData[],
): TradeHistoryParsedOrders[] => {
  let filterdTradeHistory: TradeHistoryParsedOrders[] = [];

  for (let index = 0; index < orderResponse.length; index++) {
    const {
      side,
      type,
      status,
      orderDate,
      tenNStatus,
      totalQuantity,
      orderStopPrice,
      averageFilledPrice,
      orderRequestedPrice,
      aqxExecutedQuantity,
      ...filteredObj
    } = orderResponse[index];

    const price = averageFilledPrice || INVALID_VALUE;
    const quantity = mapOrderQuantity(tenNStatus, totalQuantity, aqxExecutedQuantity);

    filterdTradeHistory.push({
      ...filteredObj,
      statusEnum: status,
      tenNParsedStatus: setStatus(tenNStatus),
      type: mapOrderType(side, type),
      orderDate: parseDateToDateAndMonth(orderResponse[index].aqxLastExecutionDate
        || orderResponse[index].lastUpdatedDate),
      color: handleColors(setStatus(tenNStatus)),
      quantity,
      price: formatCurrency(price),
      orderValue: price !== INVALID_VALUE
        ? formatCurrency((Number(quantity) * Number(price)).toString())
        : INVALID_VALUE,
    });
  }

  return filterdTradeHistory;
};

export const handleColors = (status: string): string => {
  switch (status) {
    case lang.commonOrderStatusFilterFilled(): return COLORS.green;
    case lang.commonMyAccountPendingOrdersPartiallFill(): return COLORS.warning;
    default: return COLORS.red;
  }
};

export const isOrderPartialyFill = (tenNStatus: TenNSOROrderStatus): boolean => (
  tenNStatus === TenNSOROrderStatus.PartialFill
);

export const setStatus = (status: TenNSOROrderStatus): string => {
  switch (status) {
    case TenNSOROrderStatus.Pending: return lang.commonOrderStatusPending();
    case TenNSOROrderStatus.PartialFill: return lang.commonMyAccountPendingOrdersPartiallFill();
    case TenNSOROrderStatus.PartialReject: return lang.commonMyAccountPendingOrdersPartialReject();
    case TenNSOROrderStatus.Filled: return lang.commonOrderStatusFilterFilled();
    case TenNSOROrderStatus.Cancelled: return lang.commonOrderStatusFilterCancelled();
    case TenNSOROrderStatus.Rejected: return lang.commonOrderStatusRejected();
    case TenNSOROrderStatus.Expired: return lang.commonOrderStatusFilterExpired();
    default: return lang.commonOrderStatusUndefined();
  }
};

export const mapOrderQuantity = (tenNStatus: TenNSOROrderStatus, totalQuantity: number, aqxExecutedQuantity: number):
string | number => {
  const totalQuantityText = formatNumber(totalQuantity, true);
  const partialFillText = `${formatNumber(aqxExecutedQuantity ?? 0, true)} / ${totalQuantityText}`;


  if (setStatus(tenNStatus) === lang.commonMyAccountPendingOrdersPartiallFill()
  || setStatus(tenNStatus) === lang.commonMyAccountPendingOrdersPartialReject()) {
    return partialFillText;
  }
  if (setStatus(tenNStatus) === lang.commonOrderStatusFilterExpired()
  || setStatus(tenNStatus) === lang.commonOrderStatusFilterCancelled()) {
    return !aqxExecutedQuantity ? totalQuantityText : partialFillText;
  }

  return totalQuantityText;
};

/**
 * Returns a value in format 'DD MMMM' -> for example - 13 May
 */
export const parseDateToDateAndMonth = (value: string): string => moment(value).format(displayDateFormatNoYear);

export const calculateOrderValue = (price: number, quantity: number): string => (
  formatCurrency(price * quantity)
);

export const hasPartialFillDetail = (tenNStatus: string, filledQuantity: number, totalQuantity: number) => (
  tenNStatus === lang.commonMyAccountPendingOrdersPartiallFill()
  || tenNStatus === lang.commonMyAccountPendingOrdersPartialReject()
  || ((tenNStatus === lang.commonOrderStatusFilterExpired() || tenNStatus === lang.commonOrderStatusFilterCancelled())
  && filledQuantity && filledQuantity < totalQuantity));

export const getEndOfDayOrderExpirationDate = (countryCode: string = MarketCountryCode.UnitedStates) => {
  const today = moment.utc();

  if (isDateMarketDataWorkingDay(today, true, countryCode)) {
    return today.format(pendingOrdersDatePlacedFormat);
  }

  return getNextWorkday(null, countryCode)?.format(pendingOrdersDatePlacedFormat);
};
